import Typography from '@mui/material/Typography';
import { Button, IconButton } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigationCore } from '../../../../core/router/navigation';
import PrintIcon from '@mui/icons-material/Print';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useContainerCore } from '../../../../core/modules/container';
import { usePrintLabel } from '../../../../core/hooks/usePrintLabel';
import { useCompanyCore } from '../../../../core/modules/company';
import { useMasterCore } from '../../../../core/modules/master';
import { FilterDrawer, FilterField } from '../../shared/FilterDrawer';
import { ProductCategory } from '../../../../core/interfaces/category.interface';

interface ContainerProductsFilters {
  search?: string;
  product_category_id?: number;
}

interface HeaderProps {
  filters?: ContainerProductsFilters;
  onFiltersChange?: (filters: ContainerProductsFilters) => void;
}

const Header = ({ filters = {}, onFiltersChange }: HeaderProps) => {
  const { total, products } = useContainerCore();
  const { goToValidContainers } = useNavigationCore();
  const { printMultipleLabels } = usePrintLabel();
  const { companyId } = useCompanyCore();
  const { productCategories, getCategories } = useMasterCore();

  const [localFilters, setLocalFilters] = useState<ContainerProductsFilters>(filters);

  // Cargar categorías cuando se monte el componente
  useEffect(() => {
    if (companyId) {
      getCategories(companyId, 'product');
    }
  }, [companyId, getCategories]);

  // Sincronizar filtros locales con los externos
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFiltersChange = useCallback((newFilters: ContainerProductsFilters) => {
    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  }, [onFiltersChange]);

  const handleApplyFilters = useCallback((appliedFilters?: ContainerProductsFilters) => {
    if (appliedFilters) {
      handleFiltersChange(appliedFilters);
    }
  }, [handleFiltersChange]);

  const handleClearFilters = useCallback(() => {
    const clearedFilters: ContainerProductsFilters = {
      search: undefined,
      product_category_id: undefined,
    };
    setLocalFilters(clearedFilters);
    onFiltersChange?.(clearedFilters);
  }, [onFiltersChange]);

  const filterFields: FilterField[] = useMemo(() => [
    {
      key: 'search',
      label: 'Buscar',
      type: 'text',
      placeholder: 'Buscar por código o descripción...',
    },
    {
      key: 'product_category_id',
      label: 'Categoría',
      type: 'category',
      categories: productCategories as ProductCategory[],
    },
  ], [productCategories]);

  const print = useCallback((ev: any) => {
    ev.stopPropagation();
    // mapear filas a Article-lite para el hook
    // Usamos solo los códigos/ids y delegamos el render al hook con etiquetas de artículo
    const items = products
      .filter((p: any) => (p.product_code ?? p.code))
      .map((p: any) => ({
        id: p.product_id ?? p.id,
        name: (p.product_description ?? p.name ?? 'ARTÍCULO'),
        brand: p.brand ?? '',
        code: p.product_code ?? p.code ?? '',
        description: p.product_description ?? p.description ?? '',
      }) as any);
    if (items.length > 0) {
      printMultipleLabels(items as any, { type: 'article' });
    }
  }, [products, printMultipleLabels]);

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <div className="flex items-center">
            <IconButton
              color="secondary"
              onClick={goToValidContainers}
            >
              <ArrowBackOutlinedIcon className='mr-6' />
            </IconButton>  
            <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
              Productos
            </Typography>
          </div>
          <Typography className="text-14 font-medium" color="text.secondary">
            {total} producto/s
          </Typography>
        </div>

        <div className="flex items-center gap-4">
          <FilterDrawer
            filters={localFilters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            fields={filterFields}
            disabled={!companyId}
          />
          <Button className="mx-4 my-4 w-100" color="secondary" onClick={print}>
            <PrintIcon className="mx-2" />
            Imprimir todo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Header;
